import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wand2, Save, ArrowLeft, Calculator, Search, Download, Upload as UploadIcon, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import * as XLSX from "xlsx";

const RET_HEADS = [
  "Retention PO Terms", "IR Retention", "Safety Retention",
  "BG Retention", "GST Retention", "Other Retention",
];

const SHORT = {
  "Retention PO Terms": "PO Ret.",
  "IR Retention": "IR",
  "Safety Retention": "Safety",
  "BG Retention": "BG",
  "GST Retention": "GST",
  "Other Retention": "Other",
};

const emptyHeader = () => ({
  payment_date: new Date().toISOString().slice(0, 10),
  voucher_no: "",
  mode: "NEFT",
  cheque_no: "",
  cheque_date: "",
  party_client_name: "",
  amount: "",
  remark: "",
});

const newRow = (inv) => ({
  invoice_id: inv.id,
  invoice_number: inv.invoice_number,
  invoice_date: inv.invoice_date,
  payment_due_date: inv.payment_due_date,
  total_invoice: Number(inv.net_amount || 0),
  base_amount: Number(inv.base_amount || 0),
  // Always auto-fill TDS as 2% of Base Amount; user can edit on this page
  tds: Number((Number(inv.base_amount || 0)) * 0.02).toFixed(2),
  cess: String(inv.cess_amount ?? 0),
  ld: String(inv.ld_amount ?? 0),
  credit_note: String(inv.credit_note_amount ?? 0),
  rounding: String(inv.rounding_amount ?? 0),
  pending_retention: Number(inv.pending_retention || 0),
  total_paid: Number(inv.total_paid || 0),
  // Retentions already released to us BEFORE this voucher (sum of paid_amount across all
  // retentions on this invoice). Treated as money received → reduces outstanding.
  total_retention_paid: Number(inv.total_retention_paid || 0),
  // Capture the original (saved on invoice) value so we can detect rounding-only changes
  _orig_rounding: Number(inv.rounding_amount || 0),
  payment_amount: "",
  line_remark: "",
  // Auto-populate retention columns with current pending balance per head.
  // Decreasing a value represents a retention payment received for that head.
  retentions: Object.fromEntries(
    RET_HEADS.map((h) => [h, Number(inv.pending_by_head?.[h] || 0).toFixed(2)])
  ),
  // Baseline (pending before this voucher) — for delta calculation & UI hints
  baseline: Object.fromEntries(
    RET_HEADS.map((h) => [h, Number(inv.pending_by_head?.[h] || 0)])
  ),
});

// Live net payable per row given current tds/cess/ld/credit-note/rounding inputs
const rowNetPayable = (row) =>
  Math.round((row.total_invoice - Number(row.tds || 0) - Number(row.cess || 0) - Number(row.ld || 0) - Number(row.credit_note || 0) - Number(row.rounding || 0)) * 100) / 100;
// Live retention released by THIS voucher = sum over heads of max(0, baseline - current).
// Decreasing a retention head represents money received against that retention head.
const rowReleasedNow = (row) => {
  let released = 0;
  for (const h of RET_HEADS) {
    const base = Number(row.baseline?.[h] || 0);
    const curr = Number(row.retentions?.[h] || 0);
    const delta = base - curr;
    if (delta > 0) released += delta;
  }
  return Math.round(released * 100) / 100;
};
// Live outstanding factoring in THIS voucher's typed Pay Now AND any retention released now.
// Outstanding = net_payable - cash_paid_before - prior_retention_releases - payment_now - released_now.
// Pending retentions (not yet released) remain part of the unpaid balance.
const rowOutstanding = (row) => {
  const payNow = Number(row.payment_amount || 0);
  const released = rowReleasedNow(row);
  return Math.round(
    (rowNetPayable(row)
      - Number(row.total_paid || 0)
      - Number(row.total_retention_paid || 0)
      - payNow
      - released) * 100
  ) / 100;
};

export default function BulkPayment() {
  const nav = useNavigate();
  const { voucherNo } = useParams();
  const isEdit = Boolean(voucherNo);
  const [header, setHeader] = useState(emptyHeader());
  const [parties, setParties] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  // Bulk Excel upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/bulk-payment/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bulk_payment_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const validateUpload = async (file) => {
    setUploadBusy(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bulk-payment/upload?dry_run=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Defensive: detect responses from older backends that don't return the new validation shape
      if (!data || typeof data.ok !== "boolean" || !data.summary) {
        setUploadResult({
          ok: false,
          errors: [{ row: "—", field: "backend", message: "Server appears to be running an older build. Please restart the backend service so it picks up the new code." }],
          preview: [],
          summary: { error_count: 1, vouchers_to_create: 0, lines_to_create: 0, rows_parsed: 0 },
        });
        return;
      }
      setUploadResult(data);
    } catch (e) {
      setUploadResult({ ok: false, errors: [{ row: "—", field: "file", message: e.response?.data?.detail || "Upload failed" }], preview: [], summary: { error_count: 1, vouchers_to_create: 0, lines_to_create: 0, rows_parsed: 0 } });
    } finally {
      setUploadBusy(false);
    }
  };

  const commitUpload = async (skipErrors = false) => {
    if (!uploadFile || !uploadResult) return;
    if (!skipErrors && !uploadResult.ok) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const { data } = await api.post(`/bulk-payment/upload?dry_run=false&skip_errors=${skipErrors ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const created = (data.created_vouchers || []).map((v) => `#${v.voucher_no}`).join(", ");
      const skipped = data.skipped_lines?.length ? ` · ${data.skipped_lines.length} row(s) skipped` : "";
      if (data.created_vouchers?.length) {
        toast.success(`${data.created_vouchers.length} voucher(s) created: ${created}${skipped}`);
        setUploadOpen(false);
        setUploadFile(null);
        setUploadResult(null);
      } else {
        toast.error("Nothing committed — all rows had errors.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Commit failed");
    } finally {
      setUploadBusy(false);
    }
  };

  const downloadBulkErrorReport = () => {
    if (!uploadResult?.errors?.length) return;
    const aoa = [
      ["Row #", "Field", "Error Message"],
      ...uploadResult.errors.map((e) => [e.row, e.field || "", e.message || ""]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 8 }, { wch: 22 }, { wch: 80 }];
    ["A1", "B1", "C1"].forEach((addr) => {
      if (ws[addr]) ws[addr].s = { font: { bold: true } };
    });
    XLSX.utils.book_append_sheet(wb, ws, "Errors");
    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    XLSX.writeFile(wb, `bulk_payment_upload_errors_${ts}.xlsx`);
    toast.success("Error report downloaded");
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadFile(f);
    validateUpload(f);
  };

  // Load parties; on new voucher fetch next number, on edit load existing voucher
  useEffect(() => {
    api.get("/invoices/filters").then(({ data }) => setParties(data.party_client_name || []));
    if (isEdit) {
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/vouchers/${voucherNo}`);
          setHeader({
            payment_date: data.payment_date || "",
            voucher_no: data.voucher_no,
            mode: data.mode || "NEFT",
            cheque_no: data.cheque_no || "",
            cheque_date: data.cheque_date || "",
            party_client_name: data.party_client_name || "",
            amount: "",
            remark: data.remark || "",
          });
          // Map voucher lines to rows preserving the user's previously entered values
          const mappedRows = (data.lines || []).map((l) => ({
            invoice_id: l.invoice_id,
            invoice_number: l.invoice_number,
            invoice_date: l.invoice_date,
            payment_due_date: l.payment_due_date,
            total_invoice: Number(l.total_invoice || 0),
            base_amount: Number(l.base_amount || 0),
            tds: String(l.tds_amount ?? 0),
            cess: String(l.cess_amount ?? 0),
            ld: String(l.ld_amount ?? 0),
            credit_note: String(l.credit_note_amount ?? 0),
            rounding: String(l.rounding_amount ?? 0),
            pending_retention: 0,
            total_paid: Number(l.paid_by_others || 0),
            // In Edit mode, "paid_by_others" already excludes this voucher's payment contribution.
            // Prior retention releases (from other vouchers) are baked into `l.outstanding` via
            // the backend's adjusted_outstanding calculation, so we deliberately keep this 0 to
            // avoid double-subtraction. Instead, we anchor the row's saved outstanding through
            // the baseline retentions so rowOutstanding stays in sync.
            total_retention_paid: 0,
            payment_amount: l.payment_amount ? String(l.payment_amount) : "",
            line_remark: l.line_remark || "",
            retentions: Object.fromEntries(
              RET_HEADS.map((h) => [h, l.retentions?.[h] != null ? String(l.retentions[h]) : "0"])
            ),
            // Baseline = pending excluding this voucher's contributions
            baseline: Object.fromEntries(
              RET_HEADS.map((h) => [h, Number(l.pending_by_head_excl?.[h] || 0)])
            ),
            _in_voucher: true,
          }));
          // Also load every other outstanding invoice for this party (matches the create flow)
          let extraRows = [];
          if (data.party_client_name) {
            try {
              const { data: outstanding } = await api.get("/outstanding-invoices", {
                params: { party_client_name: data.party_client_name },
              });
              const existingIds = new Set(mappedRows.map((r) => r.invoice_id));
              extraRows = outstanding
                .filter((inv) => !existingIds.has(inv.id))
                .map((inv) => ({ ...newRow(inv), _in_voucher: false }));
            } catch (_err) {
              // Non-fatal: if outstanding fetch fails, fall back to voucher lines only
            }
          }
          setRows([...mappedRows, ...extraRows]);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to load voucher");
        } finally {
          setLoading(false);
        }
      })();
    } else {
      api.get("/voucher/next").then(({ data }) => setHeader(h => ({ ...h, voucher_no: data.voucher_no })));
    }
    // eslint-disable-next-line
  }, [voucherNo]);

  // When party changes, load outstanding invoices
  const loadOutstanding = async (party) => {
    setLoading(true);
    try {
      const { data } = await api.get("/outstanding-invoices", { params: { party_client_name: party } });
      setRows(data.map(newRow));
    } catch (e) {
      toast.error("Failed to load outstanding invoices");
    } finally {
      setLoading(false);
    }
  };

  const onPartyChange = (party) => {
    setHeader({ ...header, party_client_name: party });
    if (isEdit) return; // do not overwrite loaded rows in edit mode
    if (party) loadOutstanding(party);
    else setRows([]);
  };

  const updateRow = (idx, key, value) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  const updateRet = (idx, head, value) => {
    setRows((r) => r.map((row, i) =>
      i === idx ? { ...row, retentions: { ...row.retentions, [head]: value } } : row
    ));
  };

  // Auto Adjust: distribute a user-prompted amount across outstanding rows in order
  const autoAdjust = () => {
    const input = window.prompt("Enter the amount to distribute across outstanding invoices (₹):", "");
    if (input == null) return;
    let remaining = Number(input || 0);
    if (!remaining || remaining <= 0) {
      toast.error("Enter a positive amount to auto-adjust");
      return;
    }
    setRows((rs) =>
      rs.map((row) => {
        if (remaining <= 0) return { ...row, payment_amount: "" };
        const due = Math.max(0, rowOutstanding({ ...row, payment_amount: "" }));
        const apply = Math.min(due, remaining);
        remaining = +(remaining - apply).toFixed(2);
        return { ...row, payment_amount: apply > 0 ? apply.toFixed(2) : "" };
      })
    );
    if (remaining > 0) {
      toast.warning(`₹${remaining.toFixed(2)} unallocated — exceeds total outstanding`);
    } else {
      toast.success("Amount distributed across outstanding invoices");
    }
  };

  // Filtered visible rows based on invoice-number search
  const visibleRows = useMemo(() => {
    if (!search.trim()) return rows.map((r, idx) => ({ r, idx }));
    const q = search.trim().toLowerCase();
    return rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => (r.invoice_number || "").toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    const visible = visibleRows.map(({ r }) => r);
    const sum = (fn) => visible.reduce((s, r) => s + fn(r), 0);
    const totalInvoice = sum((r) => Number(r.total_invoice || 0));
    const tds = sum((r) => Number(r.tds || 0));
    const cess = sum((r) => Number(r.cess || 0));
    const ld = sum((r) => Number(r.ld || 0));
    const cn = sum((r) => Number(r.credit_note || 0));
    const rounding = sum((r) => Number(r.rounding || 0));
    const netPayable = sum((r) => rowNetPayable(r));
    const outstanding = sum((r) => rowOutstanding(r));
    const paid = sum((r) => Number(r.payment_amount || 0));
    // Per-head net change (target - baseline)
    const retByHead = Object.fromEntries(
      RET_HEADS.map((h) => [h, sum((r) => Number(r.retentions[h] || 0) - Number(r.baseline?.[h] || 0))])
    );
    let retHeld = 0;
    let retReleased = 0;
    for (const r of visible) {
      for (const h of RET_HEADS) {
        const d = Number(r.retentions[h] || 0) - Number(r.baseline?.[h] || 0);
        if (d > 0) retHeld += d;
        else if (d < 0) retReleased += -d;
      }
    }
    const retTotal = retHeld - retReleased;
    return { totalInvoice, tds, cess, ld, cn, rounding, netPayable, outstanding, paid, retByHead, retHeld, retReleased, retTotal, grand: paid + retReleased };
  }, [visibleRows]);

  const submit = async () => {
    if (!header.party_client_name) return toast.error("Select a party first");
    const lines = rows
      .map((r) => {
        // Send every retention head as a target value (server computes delta vs current pending).
        // A delta != 0 in any head means the voucher touches retention for that invoice.
        const retEntries = RET_HEADS.map((h) => ({
          head: h,
          amount: Number(r.retentions[h] || 0),
          remark: r.line_remark || "",
        }));
        // Determine whether this line has any meaningful change vs baseline
        const hasRetChange = RET_HEADS.some(
          (h) => Math.abs(Number(r.retentions[h] || 0) - Number(r.baseline?.[h] || 0)) > 0.005
        );
        const hasRoundingChange = Math.abs(Number(r.rounding || 0) - Number(r._orig_rounding || 0)) > 0.005;
        return {
          invoice_id: r.invoice_id,
          payment_amount: Number(r.payment_amount || 0),
          line_remark: r.line_remark,
          tds_amount: Number(r.tds || 0),
          cess_amount: Number(r.cess || 0),
          ld_amount: Number(r.ld || 0),
          credit_note_amount: Number(r.credit_note || 0),
          rounding_amount: Number(r.rounding || 0),
          retentions: retEntries,
          _has_change: hasRetChange || hasRoundingChange,
        };
      })
      .filter((l) => l.payment_amount > 0 || l._has_change)
      .map(({ _has_change, ...rest }) => rest);

    if (lines.length === 0) {
      return toast.error("Enter at least one payment or retention amount");
    }

    setBusy(true);
    try {
      const payload = {
        payment_date: header.payment_date,
        voucher_no: header.voucher_no,
        mode: header.mode,
        cheque_no: header.cheque_no,
        cheque_date: header.cheque_date,
        reference: header.cheque_no,
        party_client_name: header.party_client_name,
        remark: header.remark,
        lines,
      };
      let data;
      if (isEdit) {
        const res = await api.put(`/vouchers/${voucherNo}`, payload);
        data = res.data;
        const parts = [`${data.payments_created} payments`];
        if (data.retentions_held) parts.push(`${data.retentions_held} held`);
        if (data.retentions_released) parts.push(`${data.retentions_released} released`);
        toast.success(`Voucher ${data.voucher_no} updated · ${parts.join(" · ")}`);
        nav("/vouchers");
      } else {
        const res = await api.post("/payments/bulk", payload);
        data = res.data;
        const parts = [`${data.payments_created} payments`];
        if (data.retentions_held) parts.push(`${data.retentions_held} held`);
        if (data.retentions_released) parts.push(`${data.retentions_released} released`);
        toast.success(`Voucher ${data.voucher_no} saved · ${parts.join(" · ")}`);
        // Reset for next entry
        setHeader(emptyHeader());
        const { data: v } = await api.get("/voucher/next");
        setHeader((h) => ({ ...h, voucher_no: v.voucher_no }));
        setRows([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1800px]">
      {/* Mobile-only notice: Bulk Payment is a multi-column data-entry grid not ideal for phones */}
      <div className="md:hidden mb-3 bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 text-[12px] leading-snug flex items-start gap-2 rounded">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <strong className="font-semibold">Best viewed on a tablet or desktop.</strong> The bulk-entry grid has many columns — please swipe horizontally on the table below, or open this page on a larger screen for the full experience.
        </div>
      </div>
      <div className="flex items-end justify-between gap-6 flex-wrap mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
            {isEdit ? "Edit Voucher" : "Bulk Receipt"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
            {isEdit ? `Voucher #${voucherNo}` : "Payment & Retention Entry"}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {isEdit
              ? "Update payment / retention amounts. All outstanding invoices for this party are listed — In-Voucher rows preserve their saved values, Available rows can be opted in by typing a Pay Now or changing a retention head. Saving replaces the original entries under this voucher."
              : "Pick a party, allocate payment + retention amounts across outstanding invoices, then save once."}
          </p>
          {isEdit && rows.length > 0 && (() => {
            const inV = rows.filter((r) => r._in_voucher).length;
            const avail = rows.length - inV;
            return (
              <div className="text-[11px] text-slate-600 mt-2 flex items-center gap-2 flex-wrap" data-testid="bp-edit-counts">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded">
                  {inV} In Voucher
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded">
                  {avail} Available
                </span>
                <span className="text-slate-400">· Total {rows.length} invoice{rows.length === 1 ? "" : "s"} for {header.party_client_name}</span>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          {!isEdit && (
            <>
              <Button onClick={downloadTemplate} variant="outline" className="rounded-none h-10 border-slate-300" data-testid="bp-template-btn">
                <Download className="w-4 h-4 mr-1.5" /> Template
              </Button>
              <Button onClick={() => { setUploadOpen(true); setUploadResult(null); setUploadFile(null); }} variant="outline" className="rounded-none h-10 border-[#002FA7] text-[#002FA7] hover:bg-indigo-50" data-testid="bp-upload-btn">
                <UploadIcon className="w-4 h-4 mr-1.5" /> Upload Excel
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => nav(-1)} className="rounded-none h-10" data-testid="bp-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
        </div>
      </div>

      {/* Header form */}
      <div className="bg-white border border-slate-200 p-5 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Date *</Label>
            <Input type="date" value={header.payment_date} onChange={(e) => setHeader({ ...header, payment_date: e.target.value })} className="rounded-none mt-1" data-testid="bp-date" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Voucher No *</Label>
            <Input value={header.voucher_no} onChange={(e) => setHeader({ ...header, voucher_no: e.target.value })} className="rounded-none mt-1 font-mono-data" data-testid="bp-voucher" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Party / Customer *</Label>
            <Select value={header.party_client_name} onValueChange={onPartyChange}>
              <SelectTrigger className="rounded-none mt-1" data-testid="bp-party">
                <SelectValue placeholder="Select party" />
              </SelectTrigger>
              <SelectContent>
                {parties.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Mode</Label>
            <Select value={header.mode} onValueChange={(v) => setHeader({ ...header, mode: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="bp-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["NEFT", "RTGS", "UPI", "Cheque", "Cash", "DD", "IMPS"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Cheque / UTR</Label>
            <Input value={header.cheque_no} onChange={(e) => setHeader({ ...header, cheque_no: e.target.value })} className="rounded-none mt-1" data-testid="bp-cheque" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Payment Receipt Date</Label>
            <Input type="date" value={header.cheque_date} onChange={(e) => setHeader({ ...header, cheque_date: e.target.value })} className="rounded-none mt-1" data-testid="bp-payment-receipt-date" />
          </div>
          <div className="col-span-2 md:col-span-3 lg:col-span-4">
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Remark</Label>
            <Input value={header.remark} onChange={(e) => setHeader({ ...header, remark: e.target.value })} className="rounded-none mt-1" data-testid="bp-remark" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Voucher Amount</Label>
            <Input
              type="text"
              readOnly
              value={formatINR(totals.grand)}
              className="rounded-none mt-1 font-mono-data text-right text-base font-semibold bg-slate-50 border-slate-200"
              data-testid="bp-amount"
              tabIndex={-1}
            />
            <div className="text-[9px] text-slate-400 mt-0.5">Auto: Pay Now + Retention Released</div>
          </div>
          <div className="flex flex-col items-stretch gap-2 justify-end">
            <Button onClick={autoAdjust} variant="outline" className="rounded-none h-10 w-full" data-testid="bp-auto-adjust" disabled={!header.party_client_name || rows.length === 0}>
              <Wand2 className="w-4 h-4 mr-1.5" /> Auto Adjust
            </Button>
            <Button
              onClick={submit}
              disabled={busy || rows.length === 0}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none h-10 w-full"
              data-testid="bp-submit-top"
            >
              <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : (isEdit ? "Update Voucher" : "Save Voucher")}
            </Button>
          </div>
        </div>
      </div>

      {/* Search + count */}
      <div className="flex items-center justify-between gap-3 mb-2 mt-2">
        <div className="flex-1 max-w-sm relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input
            placeholder="Search invoice number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-none pl-9 h-9 text-sm"
            data-testid="bp-search"
            disabled={rows.length === 0}
          />
        </div>
        <div className="text-[11px] text-slate-500">
          {rows.length > 0 ? (
            search ? <span><span className="font-mono-data text-slate-900 font-semibold">{visibleRows.length}</span> of {rows.length} invoices shown</span>
                   : <span><span className="font-mono-data text-slate-900 font-semibold">{rows.length}</span> outstanding invoices</span>
          ) : null}
        </div>
      </div>

      {rows.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-[11px] text-amber-900 px-3 py-2 mb-2 flex items-center gap-2">
          <span className="font-semibold uppercase tracking-wider text-[10px]">Retention columns:</span>
          <span>Auto-filled with current pending balance. <span className="text-emerald-700 font-semibold">Decrease</span> a value to record retention payment received · <span className="text-amber-700 font-semibold">Increase</span> to hold more retention.</span>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Invoice #</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Inv. Date</th>
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Due Date</th>
              <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Total Invoice</th>
              <th className="text-right px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700 whitespace-nowrap bg-rose-50">TDS</th>
              <th className="text-right px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700 whitespace-nowrap bg-rose-50">CESS</th>
              <th className="text-right px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700 whitespace-nowrap bg-rose-50">LD</th>
              <th className="text-right px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-700 whitespace-nowrap bg-fuchsia-50" title="Credit Note Deduction — adjusted from Net Payable">CN</th>
              <th className="text-right px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700 whitespace-nowrap bg-violet-50" title="Rounding adjustment — positive value reduces Net Payable & Outstanding, negative adds back. Use to absorb paisa-level rounding when reconciling with bank receipts.">ROUND</th>
              <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Net Payable</th>
              <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Outstanding</th>
              <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[#002FA7] whitespace-nowrap bg-blue-50">Pay Now</th>
              {RET_HEADS.map(h => (
                <th key={h} className="text-right px-2 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 whitespace-nowrap bg-amber-50" title={`${h} — column is auto-filled with current pending balance. Decrease to record a retention payment received, increase to hold more retention.`}>
                  {SHORT[h]}
                </th>
              ))}
              <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Remark</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={19} className="text-center text-sm text-slate-400 py-12">Loading outstanding invoices…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={19} className="text-center text-sm text-slate-400 py-12">
                {header.party_client_name ? "No outstanding invoices for this party." : "Select a party to load outstanding invoices."}
              </td></tr>
            )}
            {!loading && rows.length > 0 && visibleRows.length === 0 && (
              <tr><td colSpan={19} className="text-center text-sm text-slate-400 py-12">
                No invoices match "{search}".
              </td></tr>
            )}
            {visibleRows.map(({ r: row, idx }) => (
              <tr
                key={row.invoice_id}
                className={`border-b border-slate-100 hover:bg-slate-50/60 ${isEdit && row._in_voucher === false ? "bg-slate-50/40" : ""}`}
                data-testid={`bp-row-${row.invoice_number}`}
              >
                <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <span>{row.invoice_number}</span>
                    {isEdit && row._in_voucher && (
                      <span
                        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded"
                        title="This invoice is already part of the voucher"
                        data-testid={`bp-in-voucher-${row.invoice_number}`}
                      >
                        In Voucher
                      </span>
                    )}
                    {isEdit && row._in_voucher === false && (
                      <span
                        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded"
                        title="Type Pay Now or change a retention head to include this invoice in the voucher"
                        data-testid={`bp-available-${row.invoice_number}`}
                      >
                        Available
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDate(row.invoice_date)}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{row.payment_due_date ? formatDate(row.payment_due_date) : "—"}</td>
                <td className="px-3 py-2 text-right font-mono-data text-slate-700 whitespace-nowrap">{formatINR(row.total_invoice)}</td>
                <td className="px-1 py-1 bg-rose-50/30">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={row.tds}
                    onChange={(e) => updateRow(idx, "tds", e.target.value)}
                    className="rounded-none h-10 min-w-[110px] text-right font-mono-data text-sm border-rose-200 px-2"
                    placeholder="0"
                    data-testid={`bp-tds-${row.invoice_number}`}
                  />
                </td>
                <td className="px-1 py-1 bg-rose-50/30">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={row.cess}
                    onChange={(e) => updateRow(idx, "cess", e.target.value)}
                    className="rounded-none h-10 min-w-[100px] text-right font-mono-data text-sm border-rose-200 px-2"
                    placeholder="0"
                    data-testid={`bp-cess-${row.invoice_number}`}
                  />
                </td>
                <td className="px-1 py-1 bg-rose-50/30">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={row.ld}
                    onChange={(e) => updateRow(idx, "ld", e.target.value)}
                    className="rounded-none h-10 min-w-[100px] text-right font-mono-data text-sm border-rose-200 px-2"
                    placeholder="0"
                    data-testid={`bp-ld-${row.invoice_number}`}
                  />
                </td>
                <td className="px-1 py-1 bg-fuchsia-50/40">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={row.credit_note}
                    onChange={(e) => updateRow(idx, "credit_note", e.target.value)}
                    className="rounded-none h-10 min-w-[100px] text-right font-mono-data text-sm border-fuchsia-200 px-2"
                    placeholder="0"
                    data-testid={`bp-cn-${row.invoice_number}`}
                  />
                </td>
                <td className="px-1 py-1 bg-violet-50/40">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={row.rounding}
                    onChange={(e) => updateRow(idx, "rounding", e.target.value)}
                    className="rounded-none h-10 min-w-[90px] text-right font-mono-data text-sm border-violet-200 px-2"
                    placeholder="0"
                    title="Rounding adjustment (positive reduces Net Payable; negative adds back)"
                    data-testid={`bp-round-${row.invoice_number}`}
                  />
                </td>
                <td className="px-3 py-2 text-right font-mono-data text-slate-900 font-semibold whitespace-nowrap">{formatINR(rowNetPayable(row))}</td>
                <td className={`px-3 py-2 text-right font-mono-data font-semibold whitespace-nowrap ${
                  rowOutstanding(row) < -0.005
                    ? "text-red-600"
                    : rowOutstanding(row) > 0.005 ? "text-amber-700" : "text-emerald-700"
                }`} title={rowOutstanding(row) < -0.005 ? "Over-allocated: payments + retentions held exceed Net Payable. Check earlier voucher entries." : undefined}>
                  {formatINR(rowOutstanding(row))}
                </td>
                <td className="px-1 py-1 bg-blue-50/40">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={row.payment_amount}
                    onChange={(e) => updateRow(idx, "payment_amount", e.target.value)}
                    className="rounded-none h-10 min-w-[130px] text-right font-mono-data text-sm border-blue-200 focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7] px-2"
                    placeholder="0"
                    data-testid={`bp-pay-${row.invoice_number}`}
                  />
                </td>
                {RET_HEADS.map(h => {
                  const baseline = Number(row.baseline?.[h] || 0);
                  const current = Number(row.retentions[h] || 0);
                  const delta = +(current - baseline).toFixed(2);
                  let badge = null;
                  if (Math.abs(delta) >= 0.01) {
                    if (delta > 0) {
                      badge = <span className="block text-[9px] text-amber-700 mt-0.5">+ hold {formatINR(delta)}</span>;
                    } else {
                      badge = <span className="block text-[9px] text-emerald-700 mt-0.5">− release {formatINR(-delta)}</span>;
                    }
                  }
                  return (
                    <td key={h} className="px-1 py-1 bg-amber-50/30">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={row.retentions[h]}
                        onChange={(e) => updateRet(idx, h, e.target.value)}
                        className="rounded-none h-10 min-w-[100px] text-right font-mono-data text-sm border-amber-200 px-2"
                        placeholder="0"
                        data-testid={`bp-ret-${row.invoice_number}-${h}`}
                      />
                      {badge}
                    </td>
                  );
                })}
                <td className="px-1 py-1">
                  <Input
                    value={row.line_remark}
                    onChange={(e) => updateRow(idx, "line_remark", e.target.value)}
                    className="rounded-none h-10 min-w-[140px] text-sm px-2"
                    placeholder="Optional"
                    data-testid={`bp-rem-${row.invoice_number}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && visibleRows.length > 0 && (
            <tfoot className="bg-slate-900 text-white">
              <tr>
                <td colSpan={3} className="px-3 py-3 text-right text-[10px] font-semibold uppercase tracking-wider">TOTAL</td>
                <td className="px-3 py-3 text-right font-mono-data font-bold whitespace-nowrap">{formatINR(totals.totalInvoice)}</td>
                <td className="px-2 py-3 text-right font-mono-data font-bold text-rose-200 whitespace-nowrap">{formatINR(totals.tds)}</td>
                <td className="px-2 py-3 text-right font-mono-data font-bold text-rose-200 whitespace-nowrap">{formatINR(totals.cess)}</td>
                <td className="px-2 py-3 text-right font-mono-data font-bold text-rose-200 whitespace-nowrap">{formatINR(totals.ld)}</td>
                <td className="px-2 py-3 text-right font-mono-data font-bold text-fuchsia-200 whitespace-nowrap">{formatINR(totals.cn)}</td>
                <td className="px-2 py-3 text-right font-mono-data font-bold text-violet-200 whitespace-nowrap">{formatINR(totals.rounding)}</td>
                <td className="px-3 py-3 text-right font-mono-data font-bold whitespace-nowrap">{formatINR(totals.netPayable)}</td>
                <td className="px-3 py-3 text-right font-mono-data font-bold text-amber-200 whitespace-nowrap">{formatINR(totals.outstanding)}</td>
                <td className="px-3 py-3 text-right font-mono-data font-bold text-blue-200 whitespace-nowrap">{formatINR(totals.paid)}</td>
                {RET_HEADS.map((h) => (
                  <td key={h} className="px-2 py-3 text-right font-mono-data font-bold text-amber-200 whitespace-nowrap">
                    {formatINR(totals.retByHead[h])}
                  </td>
                ))}
                <td className="px-3 py-3 text-right font-mono-data font-bold whitespace-nowrap">Σ {formatINR(totals.grand)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 left-0 right-0 mt-4 bg-white border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3 shadow-lg">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-slate-500" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Summary</span>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Payments</div>
            <div className="font-mono-data font-bold text-[#002FA7]">{formatINR(totals.paid)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Retention Held</div>
            <div className="font-mono-data font-bold text-amber-700">{formatINR(totals.retHeld)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Retention Released</div>
            <div className="font-mono-data font-bold text-emerald-700">{formatINR(totals.retReleased)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Total Receipt</div>
            <div className="font-mono-data font-bold text-slate-900">{formatINR(totals.grand)}</div>
          </div>
        </div>
      </div>

      {/* Bulk Excel Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) { setUploadFile(null); setUploadResult(null); } }}>
        <DialogContent className="rounded-none max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <UploadIcon className="w-5 h-5 text-[#002FA7]" /> Upload Bulk Payment Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              <strong className="text-slate-900">Step 1:</strong> Download the template (if you haven't already) · fill rows · share the same <code className="bg-slate-200 px-1">voucher_group_id</code> across rows that belong to the same voucher · save and upload.
              <br /><strong className="text-slate-900">Step 2:</strong> System validates and shows any errors. <strong className="text-slate-900">Step 3:</strong> Click "Commit" to create the vouchers.
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Excel File (.xlsx) *</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={onFilePicked} className="rounded-none mt-1" data-testid="bp-upload-file" />
              {uploadFile && (
                <div className="text-[11px] text-slate-500 mt-1">Selected: <strong>{uploadFile.name}</strong> · {Math.round(uploadFile.size / 1024)} KB</div>
              )}
            </div>

            {uploadBusy && (
              <div className="text-xs text-slate-500 italic flex items-center gap-2">
                <div className="w-3 h-3 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" /> Validating…
              </div>
            )}

            {uploadResult && (
              <div className="border border-slate-200 max-h-[400px] overflow-y-auto" data-testid="bp-upload-result">
                <div className={`px-4 py-2 text-xs font-semibold flex items-center gap-2 ${uploadResult.ok ? "bg-emerald-50 text-emerald-800 border-b border-emerald-200" : "bg-rose-50 text-rose-800 border-b border-rose-200"}`}>
                  {uploadResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {uploadResult.ok
                    ? `Ready to commit · ${uploadResult.summary?.vouchers_to_create ?? 0} voucher(s) · ${uploadResult.summary?.lines_to_create ?? 0} line(s) · ${uploadResult.summary?.rows_parsed ?? 0} data row(s)`
                    : `${uploadResult.summary?.error_count || (uploadResult.errors?.length || 0)} error${((uploadResult.errors?.length || 0) === 1 ? "" : "s")} found · ${uploadResult.summary?.rows_parsed || 0} data row(s) read`}
                </div>

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="px-4 py-3 bg-rose-50/40">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-rose-800 mb-2">Errors</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-rose-200 text-left">
                          <th className="px-2 py-1 font-semibold text-rose-900">Row</th>
                          <th className="px-2 py-1 font-semibold text-rose-900">Field</th>
                          <th className="px-2 py-1 font-semibold text-rose-900">Issue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.errors.map((er, i) => (
                          <tr key={i} className="border-b border-rose-100" data-testid={`bp-err-${i}`}>
                            <td className="px-2 py-1 font-mono-data text-rose-700">#{er.row}</td>
                            <td className="px-2 py-1 font-medium text-slate-800">{er.field}</td>
                            <td className="px-2 py-1 text-slate-700">{er.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {uploadResult.preview && uploadResult.preview.length > 0 && (
                  <div className="px-4 py-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-2">Vouchers preview</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-200 text-left">
                          <th className="px-2 py-1 font-semibold text-slate-700">Group</th>
                          <th className="px-2 py-1 font-semibold text-slate-700">Party</th>
                          <th className="px-2 py-1 font-semibold text-slate-700">Entry Date</th>
                          <th className="px-2 py-1 font-semibold text-slate-700">Mode</th>
                          <th className="px-2 py-1 font-semibold text-slate-700 text-right">Lines</th>
                          <th className="px-2 py-1 font-semibold text-slate-700 text-right">Payment Total</th>
                          <th className="px-2 py-1 font-semibold text-slate-700 text-right">Retention Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.preview.map((p, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="px-2 py-1 font-medium text-slate-900">{p.voucher_group_id}</td>
                            <td className="px-2 py-1 text-slate-700">{p.party_client_name || "—"}</td>
                            <td className="px-2 py-1 text-slate-600">{p.payment_date || "—"}</td>
                            <td className="px-2 py-1 text-slate-600">{p.mode || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono-data">{p.line_count}</td>
                            <td className="px-2 py-1 text-right font-mono-data font-semibold text-slate-900">{formatINR(p.payment_total)}</td>
                            <td className={`px-2 py-1 text-right font-mono-data font-semibold ${(p.retention_total || 0) > 0 ? "text-amber-700" : (p.retention_total || 0) < 0 ? "text-emerald-700" : "text-slate-400"}`}>
                              {p.retention_total ? `${p.retention_total > 0 ? "+" : ""}${formatINR(p.retention_total)}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="rounded-none">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
            {uploadResult && uploadResult.errors?.length > 0 && (
              <Button onClick={downloadBulkErrorReport} variant="outline" className="rounded-none border-slate-300" data-testid="bp-download-error-report">
                <Download className="w-4 h-4 mr-1.5" /> Download Error Report
              </Button>
            )}
            {uploadResult && !uploadResult.ok && uploadResult.summary?.vouchers_to_create > 0 && (
              <Button
                onClick={() => commitUpload(true)}
                disabled={uploadBusy}
                variant="outline"
                className="rounded-none border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                data-testid="bp-upload-valid-only"
                title="Skip rows with errors and commit only the clean vouchers"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                {uploadBusy ? "Working…" : "Upload Valid Rows Only"}
              </Button>
            )}
            <Button
              onClick={() => commitUpload(false)}
              disabled={uploadBusy || !uploadResult || !uploadResult.ok}
              className="bg-[#002FA7] hover:bg-[#00227A] rounded-none"
              data-testid="bp-upload-commit"
            >
              <Save className="w-4 h-4 mr-1.5" /> {uploadBusy ? "Working…" : "Commit Vouchers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
